import { useState, useEffect, useCallback, createContext, useContext } from "react";
import axios from "axios";
import { API } from "../constants";

const AuthContext = createContext(null);
const TOKEN_KEY = "trialeze_session_token";
const USER_KEY = "trialeze_user";

// Axios interceptor: attach stored token as Bearer header on EVERY request
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Restore user from localStorage immediately (no flash of login page)
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem(USER_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  // Persist user to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(USER_KEY);
    }
  }, [user]);
  
  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem(TOKEN_KEY);
    
    // No token stored — user is not logged in, skip the /me call entirely
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    
    try {
      // Explicitly pass token in header (don't rely on cookie at all)
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
    } catch (error) {
      // Token exists but /me failed — token is expired/invalid, clear everything
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    if (res.data.token) {
      localStorage.setItem(TOKEN_KEY, res.data.token);
    }
    if (res.data.user) {
      localStorage.setItem(USER_KEY, JSON.stringify(res.data.user));
    }
    setUser(res.data.user);
    return res.data;
  };

  const register = async (data) => {
    const res = await axios.post(`${API}/auth/register`, data);
    if (res.data.token) {
      localStorage.setItem(TOKEN_KEY, res.data.token);
    }
    if (res.data.user) {
      localStorage.setItem(USER_KEY, JSON.stringify(res.data.user));
    }
    setUser(res.data.user);
    return res.data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleOAuthCallback = async (sessionId) => {
    const res = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
    if (res.data.token) {
      localStorage.setItem(TOKEN_KEY, res.data.token);
    }
    if (res.data.user) {
      localStorage.setItem(USER_KEY, JSON.stringify(res.data.user));
    }
    setUser(res.data.user);
    return res.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, loginWithGoogle, handleOAuthCallback, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
